var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { memo, useState, useCallback } from "react";
import { ThemeProvider } from "styled-components";
import { isEmpty } from "lodash-es";
import { BaseBox, FlexBox } from '../../shared/Element';
import { doconTheme } from "../../../theme/default";
import SingleInput from "./SingleInput";
import Error from '../../shared/WidgetError';
import Label from '../../shared/InputLabel';
OTPInputComponent.defaultProps = {
    length: 4,
    autoFocus: true,
    isNumberInput: true,
    label: 'Enter OTP'
};
export function OTPInputComponent(props) {
    var label = props.label, length = props.length, isNumberInput = props.isNumberInput, autoFocus = props.autoFocus, disabled = props.disabled, onChangeOTP = props.onChangeOTP, inputClassName = props.inputClassName, inputStyle = props.inputStyle, rest = __rest(props, ["label", "length", "isNumberInput", "autoFocus", "disabled", "onChangeOTP", "inputClassName", "inputStyle"]);
    var _a = __read(useState(0), 2), activeInput = _a[0], setActiveInput = _a[1];
    var _b = __read(useState(Array(length).fill("")), 2), otpValues = _b[0], setOTPValues = _b[1];
    // Helper to return OTP from inputs
    var handleOtpChange = useCallback(function (otp) {
        var otpValue = otp.join("");
        onChangeOTP(otpValue);
    }, [onChangeOTP]);
    // Helper to return value with the right type: 'text' or 'number'
    var getRightValue = useCallback(function (str) {
        var changedValue = str;
        if (!isNumberInput) {
            return changedValue;
        }
        return !changedValue || /\d/.test(changedValue) ? changedValue : "";
    }, [isNumberInput]);
    // Change OTP value at focussing input
    var changeCodeAtFocus = useCallback(function (str) {
        var updatedOTPValues = __spreadArray([], __read(otpValues), false);
        updatedOTPValues[activeInput] = str[0] || "";
        setOTPValues(updatedOTPValues);
        handleOtpChange(updatedOTPValues);
    }, [activeInput, handleOtpChange, otpValues]);
    // Focus `inputIndex` input
    var focusInput = useCallback(function (inputIndex) {
        var selectedIndex = Math.max(Math.min(length - 1, inputIndex), 0);
        setActiveInput(selectedIndex);
    }, [length]);
    var focusPrevInput = useCallback(function () {
        focusInput(activeInput - 1);
    }, [activeInput, focusInput]);
    var focusNextInput = useCallback(function () {
        focusInput(activeInput + 1);
    }, [activeInput, focusInput]);
    // Handle onFocus input
    var handleOnFocus = useCallback(function (index) { return function () {
        focusInput(index);
    }; }, [focusInput]);
    // Handle onChange value for each input
    var handleOnChange = useCallback(function (e) {
        var val = getRightValue(e.currentTarget.value);
        if (!val) {
            e.preventDefault();
            return;
        }
        changeCodeAtFocus(val);
        focusNextInput();
    }, [changeCodeAtFocus, focusNextInput, getRightValue]);
    // Hanlde onBlur input
    var onBlur = useCallback(function () {
        setActiveInput(-1);
    }, []);
    // Handle onKeyDown input
    var handleOnKeyDown = useCallback(function (e) {
        switch (e.key) {
            case "Backspace":
            case "Delete": {
                e.preventDefault();
                if (otpValues[activeInput]) {
                    changeCodeAtFocus("");
                }
                else {
                    focusPrevInput();
                }
                break;
            }
            case "ArrowLeft": {
                e.preventDefault();
                focusPrevInput();
                break;
            }
            case "ArrowRight": {
                e.preventDefault();
                focusNextInput();
                break;
            }
            case " ": {
                e.preventDefault();
                break;
            }
            default:
                break;
        }
    }, [activeInput, changeCodeAtFocus, focusNextInput, focusPrevInput, otpValues]);
    var handleOnPaste = useCallback(function (e) {
        e.preventDefault();
        var pastedData = e.clipboardData
            .getData("text/plain")
            .trim()
            .slice(0, length - activeInput)
            .split("");
        if (pastedData) {
            var nextFocusIndex_1 = 0;
            var updatedOTPValues_1 = __spreadArray([], __read(otpValues), false);
            updatedOTPValues_1.forEach(function (val, index) {
                if (index >= activeInput) {
                    var changedValue = getRightValue(pastedData.shift() || val);
                    if (changedValue) {
                        updatedOTPValues_1[index] = changedValue;
                        nextFocusIndex_1 = index;
                    }
                }
            });
            setOTPValues(updatedOTPValues_1);
            setActiveInput(Math.min(nextFocusIndex_1 + 1, length - 1));
        }
    }, [activeInput, getRightValue, length, otpValues]);
    return (React.createElement(ThemeProvider, { theme: doconTheme },
        React.createElement(BaseBox, null,
            React.createElement(Label, { label: label }),
            React.createElement(FlexBox, __assign({}, rest), Array(length)
                .fill("")
                .map(function (_, index) { return (React.createElement(SingleInput, { key: "SingleInput-" + index, type: isNumberInput ? 'tel' : 'text', focus: activeInput === index, hasError: !isEmpty(rest.error), value: otpValues && otpValues[index], autoFocus: autoFocus, onFocus: handleOnFocus(index), onChange: handleOnChange, onKeyDown: handleOnKeyDown, onBlur: onBlur, onPaste: handleOnPaste, style: inputStyle, className: inputClassName, disabled: disabled })); })),
            React.createElement(Error, { error: rest.error }))));
}
var OTPInput = memo(OTPInputComponent);
export default OTPInput;
